/**
 * BaySelector Component
 *
 * A generic dropdown component for selecting a specific bay within a bay type.
 * Works with both FixedBay and SuperBay types through generic type parameter.
 * Supports custom sorting via comparator function.
 *
 * Requirements:
 * - 2.1: Display a Bay_Selector dropdown for fixed bay selection when multiple fixed bays exist
 * - 2.2: Display a Bay_Selector dropdown for super bay selection when multiple super bays exist
 * - 2.6: Display fixed bay numbers in ascending order
 * - 2.7: Display super bay names in alphabetical order
 */

import { useCallback, useId, useMemo } from 'react';
import './BaySelector.css';

/**
 * Props for the BaySelector component
 * @template T - The bay type (FixedBay or SuperBay)
 */
export interface BaySelectorProps<T> {
  /** Array of bays to display in the selector */
  readonly bays: readonly T[];
  /** Currently selected bay */
  readonly selectedBay: T;
  /** Callback fired when a different bay is selected */
  readonly onBayChange: (bay: T) => void;
  /** Function to get the display label for a bay */
  readonly getLabel: (bay: T) => string;
  /** Function to get the unique identifier for a bay */
  readonly getId: (bay: T) => string;
  /** Optional comparator function for sorting bays */
  readonly sortBy?: (a: T, b: T) => number;
  /** Optional label for the selector */
  readonly label?: string;
}

/**
 * BaySelector - Generic dropdown for bay selection
 *
 * A compact dropdown component that allows users to select a specific bay
 * from a list. The component is generic to work with both FixedBay and
 * SuperBay types.
 *
 * @template T - The bay type (FixedBay or SuperBay)
 *
 * @example
 * ```tsx
 * // For fixed bays (sorted by bay number)
 * <BaySelector
 *   bays={fixedBays}
 *   selectedBay={selectedFixedBay}
 *   onBayChange={setSelectedFixedBay}
 *   getLabel={(bay) => `Bay ${bay.bayNumber}`}
 *   getId={(bay) => bay.id}
 *   sortBy={(a, b) => a.bayNumber - b.bayNumber}
 *   label="Fixed Bay"
 * />
 *
 * // For super bays (sorted alphabetically)
 * <BaySelector
 *   bays={superBays}
 *   selectedBay={selectedSuperBay}
 *   onBayChange={setSelectedSuperBay}
 *   getLabel={(bay) => bay.name}
 *   getId={(bay) => bay.id}
 *   sortBy={(a, b) => a.name.localeCompare(b.name)}
 *   label="Super Bay"
 * />
 * ```
 */
export function BaySelector<T>({
  bays,
  selectedBay,
  onBayChange,
  getLabel,
  getId,
  sortBy,
  label,
}: BaySelectorProps<T>): JSX.Element {
  const selectId = useId();

  // Sort bays if a comparator is provided
  const sortedBays = useMemo(() => {
    if (!sortBy) {
      return bays;
    }
    return [...bays].sort(sortBy);
  }, [bays, sortBy]);

  // Handle selection change
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = event.target.value;
      const bay = bays.find((b) => getId(b) === selectedId);
      if (bay) {
        onBayChange(bay);
      }
    },
    [bays, getId, onBayChange]
  );

  const selectedId = getId(selectedBay);

  return (
    <div className="bay-selector">
      {label && (
        <label className="bay-selector__label" htmlFor={selectId}>
          {label}
        </label>
      )}
      <select
        id={selectId}
        className="bay-selector__select"
        value={selectedId}
        onChange={handleChange}
        aria-label={label ?? 'Select bay'}
      >
        {sortedBays.map((bay) => {
          const bayId = getId(bay);
          const bayLabel = getLabel(bay);
          return (
            <option key={bayId} value={bayId}>
              {bayLabel}
            </option>
          );
        })}
      </select>
    </div>
  );
}
